// REACT IMPORTS
import React, { useContext, useEffect, useMemo, useState } from "react";

// CSS
import "./ProductLibrary.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API
import { getAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import {
	Alert,
	Button,
	CircularProgress,
	IconButton,
	Snackbar,
	Tooltip,
} from "@mui/material";

// MUI ICONS
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";

// CUSTOM COMPONENTS
import ProductDialog from "./ProductDialog";
import RetireProductDialog from "./RetireProductDialog";
import BulkTagView from "./BulkTagView";
import ProductUsageDialog from "./ProductUsageDialog";
import ProductTable from "./ProductTable";

// CONSTANTS
import { fmtTime } from "./productLibraryUtils";
import { HUBSPOT_PRODUCTS_URL } from "./productLibraryUtils";

// REACT COMPONENT
const ProductLibrary = () => {
	// MSAL
	const { instance, accounts } = useMsal();

	// CONTEXT
	const employeeID = useContext(UserContext);

	// STATE
	const [data, setData] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");
	const [tab, setTab] = useState("active");
	// { product } to edit, { product: null } to create
	const [editing, setEditing] = useState(null);
	const [retiring, setRetiring] = useState(null);
	const [usageFor, setUsageFor] = useState(null);
	const [savedText, setSavedText] = useState("");

	// USE EFFECT
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Product Library",
			employeeID: employeeID,
			notes: "Accessed the Product Library page",
		}).catch(() => {});
		fetchProducts();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// API HELPER FUNCTIONS
	// refresh: skip the server cache and read fresh from HubSpot (Reload)
	const fetchProducts = async (refresh = false) => {
		setIsLoading(true);
		try {
			const response = await getAPICall(
				instance,
				accounts[0],
				`/api/product-library/products${refresh ? "?refresh=1" : ""}`,
			);
			setData(response.data);
			setError("");
		} catch (err) {
			console.error(err);
			setError(
				err?.response?.data?.error ||
					"Couldn't load the product library.",
			);
		}
		setIsLoading(false);
	};

	// TABLE
	const products = useMemo(() => data?.products || [], [data]);
	// Retired (inactive) products have their own tab
	const tabProducts = useMemo(
		() =>
			products.filter((p) => (tab === "inactive" ? !p.active : p.active)),
		[products, tab],
	);

	// Puts a saved or new product into the table
	const handleSaved = (product, changed, created) => {
		setEditing(null);
		setRetiring(null);
		if (product)
			setData((d) => ({
				...d,
				products: created
					? [product, ...d.products]
					: d.products.map((p) =>
							p.id === product.id ? { ...p, ...product } : p,
						),
			}));
		if (created) {
			setTab("active");
			setSavedText("Product created in HubSpot.");
			return;
		}
		const status = changed.find((c) => c.label === "Status");
		setSavedText(
			status && changed.length === 1
				? status.to === "Inactive"
					? "Product retired. It's on the Inactive tab."
					: "Product reactivated."
				: changed.length
					? `Saved to HubSpot: ${changed.map((c) => c.label).join(", ")}.`
					: "No changes to save.",
		);
	};

	// Swaps products from the Bulk Tag tab (as HubSpot has them now) into the
	// page, and drops ones deleted from HubSpot
	const handleBulkUpdated = (saved, missingIds = []) => {
		const byId = new Map(saved.map((p) => [p.id, p]));
		const missing = new Set(missingIds);
		setData((d) => ({
			...d,
			products: d.products
				.filter((p) => !missing.has(p.id))
				.map((p) => (byId.has(p.id) ? { ...p, ...byId.get(p.id) } : p)),
		}));
	};

	const activeCount = products.filter((p) => p.active).length;

	// RENDER
	return (
		<div className="ProductLibrary">
			<div className="productLibraryHeader">
				<div className="productLibraryHeaderTitle">
					Product Library
					<div className="productLibraryHeaderActions">
						{/* Same as the Access / Automation Logs dashboards */}
						{data?.loadedAt && (
							<span className="productLibraryUpdatedText">
								Updated {fmtTime(data.loadedAt)}
							</span>
						)}
						<Tooltip title="Refresh" arrow placement="top">
							<span className="productLibraryTooltipWrap">
								<IconButton
									onClick={() => fetchProducts(true)}
									disabled={isLoading}
									size="small"
								>
									{isLoading ? (
										<CircularProgress size={20} />
									) : (
										<RefreshIcon />
									)}
								</IconButton>
							</span>
						</Tooltip>
						<Button
							variant="outlined"
							disableElevation
							href={HUBSPOT_PRODUCTS_URL}
							target="_blank"
							rel="noreferrer"
							endIcon={<OpenInNewIcon />}
						>
							Products in HubSpot
						</Button>
						{data?.canEdit && (
							<Button
								variant="contained"
								disableElevation
								startIcon={<AddIcon />}
								onClick={() => setEditing({ product: null })}
							>
								New Product
							</Button>
						)}
					</div>
				</div>
			</div>
			<ProductDialog
				open={Boolean(editing)}
				product={editing?.product || null}
				options={data?.options}
				onClose={() => setEditing(null)}
				onSaved={handleSaved}
			/>
			<ProductUsageDialog
				product={usageFor}
				onClose={() => setUsageFor(null)}
			/>
			<RetireProductDialog
				product={retiring}
				onClose={() => setRetiring(null)}
				onSaved={handleSaved}
			/>
			<Snackbar
				open={Boolean(savedText)}
				autoHideDuration={4000}
				onClose={() => setSavedText("")}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="success" onClose={() => setSavedText("")}>
					{savedText}
				</Alert>
			</Snackbar>

			{error && <div className="productLibraryError">{error}</div>}

			{!data && isLoading ? (
				<div className="productLibraryLoading">
					<CircularProgress color="primary" />
					<span className="productLibraryMuted">
						Loading products and this year's usage from HubSpot…
					</span>
				</div>
			) : (
				data && (
					<>
						<div className="productLibraryTabs">
							{[
								["active", `Active (${activeCount})`],
								[
									"inactive",
									`Inactive (${products.length - activeCount})`,
								],
								...(data.canEdit ? [["bulk", "Bulk Tag"]] : []),
							].map(([id, label]) => (
								<button
									key={id}
									type="button"
									className={`productLibraryTab${tab === id ? " productLibraryTab--active" : ""}`}
									onClick={() => setTab(id)}
								>
									{label}
								</button>
							))}
						</div>
						{tab === "bulk" ? (
							<BulkTagView
								products={products}
								options={data.options}
								onUpdated={handleBulkUpdated}
								onMessage={setSavedText}
							/>
						) : (
							<ProductTable
								products={tabProducts}
								options={data.options}
								canEdit={data.canEdit}
								emptyText={
									tab === "inactive"
										? "No retired products. Retire a product from the Active tab to move it here."
										: "No active products."
								}
								onUsage={setUsageFor}
								onEdit={(product) => setEditing({ product })}
								onRetire={setRetiring}
							/>
						)}
					</>
				)
			)}
		</div>
	);
};

export default ProductLibrary;
